// src/screens/DevCeremonyGallery.js
// ============================================================================
// DEV-ONLY ceremony scenarios for /dev/gallery.
//
// The match-flow gallery renders modals through MatchReadyProvider's mockState;
// the ceremony surfaces are their own tree, so they get their own scenario map
// and a small host component. Same contract as the rest of the gallery:
//   /dev/gallery?scenario=<key>&capture=1
// one URL per screenshot, deterministic frame (phaseElapsedMs is pinned), no
// network — useEventCeremony's mockState path disables every listener and poll.
//
// This is also how the NEXT demo video gets shot: these are the real components
// the live event drives, not re-renders of the HTML mockups.
// ============================================================================

import React from 'react';
import { View, StyleSheet } from 'react-native';

import useEventCeremony from '../hooks/useEventCeremony';
import CeremonyStage from '../components/ceremony/CeremonyStage';
import { CEREMONY_PHASE, POV_STATE } from '../constants/ceremony';

// The GT7 Showdown grid — the same 16 IGNs and finishing order the approved
// demo screens were built from, so a capture is comparable frame to frame.
const GRID = [
  { ign: 'RaceR xXx', rank: 1, crits: 576 },
  { ign: 'Milioli', rank: 2, crits: 360 },
  { ign: 'Gooloo', rank: 3, crits: 216 },
  { ign: 'CritCash', rank: 4, crits: 72 },
  { ign: 'Jesusjulie', rank: 5, crits: 72 },
  { ign: 'Fabbres', rank: 6, crits: 72 },
  { ign: 'MM Racing Guy', rank: 7, crits: 72 },
  { ign: 'ADONAIGT', rank: 8, crits: 0 },
  { ign: 'Mateoracing', rank: 9, crits: 0 },
  { ign: 'C1ean Racer', rank: 10, crits: 0 },
  { ign: 'Senninha', rank: 11, crits: 0 },
  { ign: 'Garewal', rank: 12, crits: 0 },
  { ign: 'Cpalm', rank: 13, crits: 0 },
  { ign: 'Danez_echo1', rank: 14, crits: 0 },
  { ign: 'Romero', rank: 15, crits: 0 },
  { ign: 'GTBBR', rank: null, crits: 0, dnf: true },  // the rage quitter
];

// Grid (pole) order for the pre-race screens, which is not finishing order.
const POLE = [
  'RaceR xXx', 'CritCash', 'Gooloo', 'Senninha', 'Milioli', 'Jesusjulie',
  'MM Racing Guy', 'Danez_echo1', 'Garewal', 'Fabbres', 'C1ean Racer',
  'Romero', 'Cpalm', 'ADONAIGT', 'Mateoracing', 'GTBBR',
];

// Warzone BR tops out at 150 players (min 50 to launch) per constants/gameData.
// The live platform clamp is lower while BR scoring is manual; the ceremony is
// built for the real ceiling so it does not break when that clamp lifts.
const BR_N = 150;

const baseState = (over = {}) => ({
  found: true,
  eventId: 'demo-ceremony',
  ceremonyEnabled: true,
  title: 'GT7 SHOWDOWN',
  strapline: 'RED BULL RING  ·  5 LAPS  ·  1,440 CRIT POOL',
  gameId: 'gt7',
  status: 'locked',
  entryCrits: 100,
  potCrits: 1600,
  netPoolCrits: 1440,
  totalRequired: 16,
  readyCount: 0,
  resultsFinal: false,
  field: [],
  ...over,
});

const preRaceField = (readyCount) =>
  POLE.map((ign, i) => ({
    uid: `demo-${i}`,
    name: ign,
    ign,
    slot: i + 1,
    ready: i < readyCount,
    // Descending so the most recent ready is the last one turned on.
    readyAtMs: i < readyCount ? 1000 + i * 450 : null,
    state: POV_STATE.RACING,
    finalRank: null,
    crits: null,
  }));

const resultField = () =>
  GRID.map((r, i) => ({
    uid: `demo-${i}`,
    name: r.ign,
    ign: r.ign,
    slot: i + 1,
    ready: true,
    readyAtMs: 1000,
    state: r.dnf ? POV_STATE.DNF : POV_STATE.FINISHED,
    finalRank: r.rank,
    crits: r.crits || null,
  }));

export const CEREMONY_SCENARIOS = {
  ceremony_countdown: {
    title: 'Ceremony — Countdown (registration closed)',
    mock: () => ({
      phase: CEREMONY_PHASE.COUNTDOWN,
      ...baseState({
        field: preRaceField(0),
        status: 'locked',
        lockedAtMs: Date.now() - 3 * 60 * 1000,
        startsAtMs: Date.now() + 7 * 60 * 1000,
      }),
    }),
  },
  ceremony_starting_soon: {
    title: 'Ceremony — STARTING SOON + live ready ticker (11/16)',
    mock: () => ({
      phase: CEREMONY_PHASE.STARTING_SOON,
      ...baseState({
        field: preRaceField(11),
        readyCount: 11,
        startsAtMs: Date.now() + 3 * 60 * 1000,
      }),
    }),
  },
  ceremony_all_ready: {
    title: 'Ceremony — ALL PLAYERS READY (16/16)',
    mock: () => ({
      phase: CEREMONY_PHASE.STARTING_SOON,
      ...baseState({
        field: preRaceField(16),
        readyCount: 16,
        startsAtMs: Date.now() + 90 * 1000,
      }),
    }),
  },
  ceremony_grid_lock: {
    title: 'Ceremony — locking in (dice landing)',
    mock: () => ({
      phase: CEREMONY_PHASE.GRID_LOCK,
      phaseElapsedMs: 3400,
      ...baseState({ field: preRaceField(16), readyCount: 16 }),
    }),
  },
  ceremony_grid_locked: {
    title: 'Ceremony — LOCKED IN stamp',
    mock: () => ({
      phase: CEREMONY_PHASE.GRID_LOCK,
      phaseElapsedMs: 6800,
      ...baseState({ field: preRaceField(16), readyCount: 16 }),
    }),
  },
  ceremony_pov_finished: {
    title: 'Ceremony — POV result (player finished P3)',
    mock: () => ({
      phase: CEREMONY_PHASE.LIVE,
      ...baseState({ status: 'live', field: resultField() }),
      _selectedUid: 'demo-2',
    }),
  },
  ceremony_pov_quit: {
    title: 'Ceremony — POV awaiting results (rage quit)',
    mock: () => ({
      phase: CEREMONY_PHASE.LIVE,
      ...baseState({ status: 'live', field: resultField() }),
      _selectedUid: 'demo-15',
    }),
  },
  // Same surfaces, a battle-royale-sized field — the layout has to compress
  // rather than run off the canvas. Warzone BR events are 50-up.
  ceremony_br_field: {
    title: 'Ceremony — 150-player field (Warzone BR max)',
    mock: () => {
      const field = Array.from({ length: BR_N }, (_, i) => ({
        uid: `br-${i}`,
        name: `Operator${i + 1}`,
        ign: `Operator${i + 1}`,
        slot: i + 1,
        ready: i < Math.round(BR_N*0.76),
        readyAtMs: i < Math.round(BR_N*0.76) ? 1000 + i * 120 : null,
        state: POV_STATE.RACING,
        finalRank: null,
        crits: null,
      }));
      return {
        phase: CEREMONY_PHASE.STARTING_SOON,
        ...baseState({ title: 'WARZONE SHOWDOWN', strapline: 'VERDANSK  ·  SOLOS', field, readyCount: Math.round(BR_N*0.76), totalRequired: BR_N }),
        startsAtMs: Date.now() + 2 * 60 * 1000,
      };
    },
  },
  ceremony_br_grid_lock: {
    title: 'Ceremony — 150-player locking in',
    mock: () => {
      const field = Array.from({ length: BR_N }, (_, i) => ({
        uid: `br-${i}`,
        name: `Operator${i + 1}`,
        ign: `Operator${i + 1}`,
        slot: i + 1,
        ready: true,
        readyAtMs: 1000,
        state: POV_STATE.RACING,
        finalRank: null,
        crits: null,
      }));
      return {
        phase: CEREMONY_PHASE.GRID_LOCK,
        phaseElapsedMs: 4000,
        ...baseState({ title: 'WARZONE SHOWDOWN', strapline: 'VERDANSK  ·  SOLOS', field, readyCount: BR_N, totalRequired: BR_N }),
      };
    },
  },
  ceremony_h2h: {
    title: 'Ceremony — 2-player H2H standings',
    mock: () => ({
      phase: CEREMONY_PHASE.RESULTS,
      phaseElapsedMs: 6000,
      ...baseState({
        title: 'SF6 HEAD TO HEAD',
        strapline: null,
        status: 'completed',
        resultsFinal: true,
        totalRequired: 2,
        readyCount: 2,
        potCrits: 200,
        netPoolCrits: 180,
        field: [
          { uid: 'h1', name: 'CritSeeker', ign: 'CritSeeker', slot: 1, ready: true, state: POV_STATE.FINISHED, finalRank: 1, crits: 180 },
          { uid: 'h2', name: 'NightShade77', ign: 'NightShade77', slot: 2, ready: true, state: POV_STATE.FINISHED, finalRank: 2, crits: null },
        ],
      }),
    }),
  },
  ceremony_results: {
    title: 'Ceremony — final standings + Crits',
    mock: () => ({
      phase: CEREMONY_PHASE.RESULTS,
      phaseElapsedMs: 6000,
      ...baseState({
        status: 'completed',
        resultsFinal: true,
        readyCount: 16,
        field: resultField(),
      }),
    }),
  },
  ceremony_results_provisional: {
    title: 'Ceremony — standings before payouts settle',
    mock: () => ({
      phase: CEREMONY_PHASE.RESULTS,
      phaseElapsedMs: 6000,
      ...baseState({
        status: 'completed',
        resultsFinal: false,
        readyCount: 16,
        field: resultField().map((r) => ({ ...r, crits: null })),
        // Pre-split: the provisional line carries the building crowd total.
        rumbleTotalCrits: 202,
      }),
    }),
  },
  ceremony_results_rumble: {
    title: 'Ceremony — standings + crowd Rumble split (every finisher +13)',
    mock: () => ({
      phase: CEREMONY_PHASE.RESULTS,
      phaseElapsedMs: 6000,
      ...baseState({
        status: 'completed',
        resultsFinal: true,
        readyCount: 16,
        field: resultField(),
        // The 08-07 live numbers: 20,200 cents of boosts, 13 Crits x 15
        // finishers, rage-quit DNF excluded. P4 reads "72 +13", unpaid
        // finishers "— +13", GTBBR stays OUT.
        rumbleTotalCrits: 202,
        rumbleCritsEach: 13,
      }),
    }),
  },
};

export default function CeremonyScenarioHost({ scenarioKey }) {
  const scenario = CEREMONY_SCENARIOS[scenarioKey];
  const mockState = React.useMemo(
    () => (scenario ? scenario.mock() : null),
    [scenarioKey],
  );

  const ceremony = useEventCeremony('demo-ceremony', {
    mockState,
    enabled: true,
    hasLiveFeed: mockState?.status === 'live',
    selectedUid: mockState?._selectedUid || null,
  });

  if (!scenario) return null;

  return (
    <View style={styles.stage}>
      <CeremonyStage
        ceremony={ceremony}
        eventId="demo-ceremony"
        onSwitchCamera={() => {}}
        canSwitchCamera
      />
    </View>
  );
}

const styles = StyleSheet.create({
  // 16:9 so the scaled 1920x1080 canvas fills it exactly, matching what the
  // video area looks like in a real fullscreen watch session.
  stage: {
    flex: 1,
    backgroundColor: '#000',
    position: 'relative',
    aspectRatio: 16 / 9,
    alignSelf: 'center',
    width: '100%',
  },
});
